import React, { Component } from "react";
import PropTypes from "prop-types";
import Log, { TapLog } from "../../lib/log";
import { Redirect, Link } from "react-router-dom";
import { createToast } from "screens/shared/toast";
import connectComponent from "../../redux/connectComponent";
import Errors, { setError, Error, errorContains } from "components/errors";
import PasswordField from "screens/shared/forms/passwordField";
import withWrap from "components/withWrap";
import {
  BoxForm,
  BoxHeadline,
  InputForm,
  ForgotPasswordLink,
  WButton,
  Divider,
  AlternativeOption
} from "components/BoxForm";
import tw from "twin.macro";
import styled from "styled-components";

const InputMessageWarning = styled.p`
  font-family: "Lato", Helvetica, sans-serif;
  font-size: 14.4px;
  color: #e18925;
  padding-bottom: 0;
  margin-bottom: 0;
  padding-top: 8px;
`;

const SubmitButton = styled(WButton)`
  ${tw`mt-4`}
`;

const withEvent = (event) => (callback) =>
  [event.preventDefault(), callback(event), false].pop();

export class Auth extends Component {
  static propTypes = {
    actions: PropTypes.object,
    onAuth: PropTypes.func,
    onIncomplete: PropTypes.func,
    confirmationPath: PropTypes.string,
    registrationPath: PropTypes.string,
    referrer: PropTypes.string
  };

  state = {
    error: null,
    errors: {},
    disabled: false
  };

  constructor(props) {
    super(props);
    this.emailRef = React.createRef();
    this.passwordRef = React.createRef();
  }

  onLoginHandler = (event) =>
    withEvent(event)(() => this.onLogin(this.formValues()));

  resetErrors = () =>
    this.setState({
      errors: {},
      error: null
    });

  onLogin = (userValues) => {
    if (this.state.disabled) return;

    this.setState({ disabled: true });

    this.resetErrors();

    return this.props.actions
      .login(userValues)
      .then(this.onSuccess)
      .catch(this.onFailure);
  };

  onSuccess = (user) => {
    Log("onLoginSuccess", user);

    this.props.actions
      .refresh()
      .then((user) => {
        this.props.actions.optimizeEvent("login");
        this.setState({ disabled: false });
        this.goToDestination(user);
      })
      .catch(TapLog("Error Checking Authentication"));
  };

  onFailure = (errorResponse) => {
    this.setState({ disabled: false });
    return setError(this.setState.bind(this))(errorResponse);
  };

  isProfileComplete = (user) => {
    if (!user) return false;
    const hasName = user.name && user.name.trim().length >= 3;
    const hasBasicFields = user.street && user.phone && user.postal_code;
    const hasRealEmail =
      user.email && !user.email.includes("@placeholder.dwellsocial.com");
    return hasName && hasBasicFields && hasRealEmail;
  };

  goToDestination = (user) => {
    if (!this.isProfileComplete(user)) {
      this.props.onIncomplete(user);
    } else if (this.props.onAuth) {
      this.props.onAuth();
    }
  };

  shouldGoToConfirmation = (state) => errorContains(state, /confirm/);

  shouldGoToRegister = (state) => errorContains(state, /exist/);

  goToConfirmation = () => <Redirect to={this.confirmationPath(this.props)} />;

  credentialError = (state) => errorContains(state, /credential|No Session/im);

  formValues = () => ({
    email: this.emailRef.current.value,
    password: this.passwordRef.current.value
  });

  confirmationPath = (props) =>
    props.confirmationPath || "/registration/confirmation";

  registrationPath = (props) => props.registrationPath || "/registration";

  renderNavigationPrompt = (props, state) => {
    if (this.shouldGoToRegister(state)) {
      return (
        <InputMessageWarning>
          You have not signed up with this email! &nbsp;
          <Link to={this.registrationPath(props)}>Register today!</Link>
        </InputMessageWarning>
      );
    }

    return "";
  };

  render() {
    if (this.shouldGoToConfirmation(this.state)) {
      createToast("You must confirm your account to login!");
      return this.goToConfirmation();
    }

    return (
      <BoxForm
        id="new_user"
        onSubmit={this.onLoginHandler}
        acceptCharset="UTF-8"
      >
        <BoxHeadline>Sign in</BoxHeadline>

        {this.renderNavigationPrompt(this.props, this.state)}
        <Error
          error={this.credentialError(this.state) ? this.state.error : ""}
        />
        <InputForm>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            name="Email"
            id="email"
            autoFocus="autofocus"
            ref={this.emailRef}
            required="required"
          />
          <Errors errors={this.state.errors.email} />
        </InputForm>

        <PasswordField
          inputRef={this.passwordRef}
          errors={this.state.errors.password}
        />

        <ForgotPasswordLink as={Link} to="/password/new">
          Forgot your password?
        </ForgotPasswordLink>

        <SubmitButton
          value={this.state.disabled ? "Signing In..." : "Sign In"}
          type="submit"
          data-testid="signin"
        />

        <Divider />

        <AlternativeOption>
          <Link to="/session">Sign in with a code instead</Link>
        </AlternativeOption>

        <AlternativeOption>
          Don&#39;t have an account? &nbsp;
          <Link
            to={{
              pathname: this.props.registrationPath || "/registration",
              state: { referrer: this.props.referrer }
            }}
          >
            Sign up
          </Link>
        </AlternativeOption>
      </BoxForm>
    );
  }
}

export default withWrap(connectComponent(Auth, ["authentication", "users"]));
